/**
 * User List Page
 *
 * Displays all users within the tenant with search, filter, and pagination.
 * Capability-gated to users with admin:users capability.
 */

import { useQuery } from "@tanstack/react-query";
import type { Environment, UserStatus } from "@tatvex/types";
import {
  Plus,
  Search,
  Loader2,
  AlertCircle,
  RefreshCw,
  User,
  ChevronRight,
  Shield,
  Globe,
  Mail,
} from "lucide-react";
import type { ReactElement, ChangeEvent } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type { TenantUserView, UserListResponse, UserListFilters } from "./types";
import {
  USER_STATUS_INFO,
  SYSTEM_ROLE_INFO,
  ENVIRONMENT_INFO,
  ALL_ENVIRONMENTS,
  SYSTEM_ROLES,
} from "./types";

export function UserListPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:users")) {
    return <Navigate to="/studio" replace />;
  }

  return <UserListContent />;
}

function UserListContent(): ReactElement {
  const [filters, setFilters] = useState<UserListFilters>({
    search: "",
    status: "all",
    role: "all",
    environment: "all",
    page: 1,
    pageSize: 20,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["users", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set("search", filters.search);
      if (filters.status && filters.status !== "all") params.set("status", filters.status);
      if (filters.role && filters.role !== "all") params.set("role", filters.role);
      if (filters.environment && filters.environment !== "all")
        params.set("environment", filters.environment);
      if (filters.page) params.set("page", String(filters.page));
      if (filters.pageSize) params.set("pageSize", String(filters.pageSize));

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await apiRequest<UserListResponse>(`/api/v1/admin/users${query}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  }, []);

  const handleStatusChange = useCallback((e: ChangeEvent<HTMLSelectElement>): void => {
    setFilters((prev) => ({
      ...prev,
      status: e.target.value as UserStatus | "all",
      page: 1,
    }));
  }, []);

  const handleRoleChange = useCallback((e: ChangeEvent<HTMLSelectElement>): void => {
    setFilters((prev) => ({
      ...prev,
      role: e.target.value,
      page: 1,
    }));
  }, []);

  const handleEnvironmentChange = useCallback((e: ChangeEvent<HTMLSelectElement>): void => {
    setFilters((prev) => ({
      ...prev,
      environment: e.target.value as Environment | "all",
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((newPage: number): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="mt-1 text-foreground-muted">Manage users, roles, and environment access</p>
        </div>
        <Link to="/studio/admin/users/invite" className="btn btn-primary">
          <Plus className="mr-2 h-4 w-4" />
          Invite User
        </Link>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-64 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            placeholder="Search users..."
            value={filters.search ?? ""}
            onChange={handleSearchChange}
            className="w-full rounded-md border border-border bg-background py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Status filter */}
        <select
          value={filters.status ?? "all"}
          onChange={handleStatusChange}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>

        {/* Role filter */}
        <select
          value={filters.role ?? "all"}
          onChange={handleRoleChange}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Roles</option>
          {SYSTEM_ROLES.map((role) => (
            <option key={role} value={role}>
              {SYSTEM_ROLE_INFO[role]?.label ?? role}
            </option>
          ))}
        </select>

        {/* Environment filter */}
        <select
          value={filters.environment ?? "all"}
          onChange={handleEnvironmentChange}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Environments</option>
          {ALL_ENVIRONMENTS.map((env) => (
            <option key={env} value={env}>
              {ENVIRONMENT_INFO[env].label}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load users"}
          onRetry={refetch}
        />
      ) : data?.items.length === 0 ? (
        <EmptyState
          hasFilters={Boolean(
            filters.search ||
            (filters.status && filters.status !== "all") ||
            (filters.role && filters.role !== "all") ||
            (filters.environment && filters.environment !== "all")
          )}
        />
      ) : (
        <>
          {/* User table */}
          <div className="rounded-lg border border-border bg-background-secondary overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Roles
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Environments
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Last Login
                  </th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data?.items.map((user) => (
                  <UserRow key={user.userId} user={user} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.total > (filters.pageSize ?? 20) && (
            <Pagination
              page={filters.page ?? 1}
              pageSize={filters.pageSize ?? 20}
              total={data.total}
              hasMore={data.hasMore}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}

interface UserRowProps {
  readonly user: TenantUserView;
}

function UserRow({ user }: UserRowProps): ReactElement {
  const statusInfo = USER_STATUS_INFO[user.status];

  return (
    <tr className="group hover:bg-background transition-colors">
      <td className="px-4 py-3">
        <Link to={`/studio/admin/users/${user.externalId}`} className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <User className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-foreground group-hover:text-primary transition-colors">
              {user.name}
            </p>
            <p className="flex items-center gap-1 text-sm text-foreground-muted">
              <Mail className="h-3 w-3" />
              {user.email}
            </p>
          </div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            statusInfo.color === "success"
              ? "bg-success/10 text-success"
              : statusInfo.color === "warning"
                ? "bg-warning/10 text-warning"
                : "bg-foreground-muted/10 text-foreground-muted"
          }`}
        >
          {statusInfo.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {user.roles.map((role) => (
            <span
              key={role}
              className="inline-flex items-center gap-1 rounded bg-background-secondary px-2 py-0.5 text-xs"
            >
              <Shield className="h-3 w-3 text-foreground-muted" />
              {SYSTEM_ROLE_INFO[role]?.label ?? role}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {user.environments.map((env) => (
            <span
              key={env}
              className="inline-flex items-center gap-1 rounded bg-background-secondary px-2 py-0.5 text-xs"
            >
              <Globe className="h-3 w-3 text-foreground-muted" />
              {ENVIRONMENT_INFO[env].label}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-foreground-muted">
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : "Never"}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/studio/admin/users/${user.externalId}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="h-5 w-5 text-foreground-muted" />
        </Link>
      </td>
    </tr>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading users...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

interface EmptyStateProps {
  readonly hasFilters: boolean;
}

function EmptyState({ hasFilters }: EmptyStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <User className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">
        {hasFilters ? "No users match your filters" : "No users yet"}
      </h3>
      <p className="mt-2 text-foreground-muted">
        {hasFilters
          ? "Try adjusting your search or filters"
          : "Invite your first user to get started."}
      </p>
      {!hasFilters && (
        <Link to="/studio/admin/users/invite" className="btn btn-primary mt-4">
          <Plus className="mr-2 h-4 w-4" />
          Invite User
        </Link>
      )}
    </div>
  );
}

// ============================================================================
// Pagination Component
// ============================================================================

interface PaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly onPageChange: (page: number) => void;
}

function Pagination({
  page,
  pageSize,
  total,
  hasMore,
  onPageChange,
}: PaginationProps): ReactElement {
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-sm text-foreground-muted">
        Showing {start} to {end} of {total} users
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-foreground-muted">
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasMore}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
